import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@ascend/shared/components';
import { Button } from '@openzeppelin/ui-components';
import { ADMIN_DASHBOARD_FACILITIES } from '../data/mockAdapter';

type FacilityStatus = 'active' | 'paused' | 'shutdown';

interface Facility {
  id: string;
  name: string;
  status: FacilityStatus;
  tvl: string;
  utilization: string;
  activeAccounts: number;
  interestRate: string;
  warnings: number;
  defaults: number;
}

const facilities: Facility[] = ADMIN_DASHBOARD_FACILITIES;

const PAGE_SIZE_OPTIONS = [9, 18, 36] as const;

const statusConfig: Record<FacilityStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
  paused: { label: 'Paused', className: 'bg-yellow-100 text-yellow-700' },
  shutdown: { label: 'Shutdown', className: 'bg-red-100 text-red-700' },
};

type StatusFilter = 'all' | FacilityStatus;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'shutdown', label: 'Shutdown' },
];

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

// ─── Page Component ──────────────────────────────────────────────────────────

export function CreditFacilitiesPage() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const filteredFacilities = useMemo(() => {
    let result = facilities;
    if (statusFilter !== 'all') {
      result = result.filter((f) => f.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (f) => f.name.toLowerCase().includes(q) || f.id.toLowerCase().includes(q),
      );
    }
    return result;
  }, [searchQuery, statusFilter]);

  const totalPages = Math.ceil(filteredFacilities.length / pageSize);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const paginatedFacilities = filteredFacilities.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );
  const rangeStart = filteredFacilities.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, filteredFacilities.length);

  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilter, number> = { all: facilities.length, active: 0, paused: 0, shutdown: 0 };
    for (const f of facilities) counts[f.status]++;
    return counts;
  }, []);

  const totalTvl = useMemo(() => facilities.reduce((sum, f) => sum + parseFloat(f.tvl.replace(/[$M]/g, '')), 0), []);
  const avgUtilization = useMemo(() => {
    const active = facilities.filter((f) => f.status !== 'shutdown');
    if (active.length === 0) return 0;
    return active.reduce((sum, f) => sum + parseFloat(f.utilization), 0) / active.length;
  }, []);
  const totalAccounts = useMemo(() => facilities.reduce((sum, f) => sum + f.activeAccounts, 0), []);
  const activeFacilities = useMemo(() => facilities.filter((f) => f.status === 'active').length, []);

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:building-2" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Credit Facilities</h1>
            <p className="text-sm text-muted-foreground">
              Overview of your deployed credit facilities and their status.
            </p>
          </div>
        </div>
        <Button onClick={() => navigate('/deploy-cf-wizard')}>
          <Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="h-4 w-4" />
          Deploy New Facility
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard title="Total Facilities" value={`${facilities.length}`} subtitle={`${activeFacilities} active`} icon={<Icon name="streamline:building-2" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard title="Total TVL" value={`$${totalTvl.toFixed(1)}M`} subtitle="Across all facilities" icon={<Icon name="streamline:money-cash-coins-stack-accounting-billing-payment-stack-cash-coins-currency-money-finance" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard title="Avg Utilization" value={`${Math.round((avgUtilization + Number.EPSILON) * 100) / 100}%`} subtitle="Pool utilization rate" icon={<Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-5 w-5 text-muted-foreground" />} />
        <StatCard
          title="Accounts"
          value={totalAccounts.toString()}
          icon={<Icon name="streamline:interface-user-multiple-close-geometric-human-multiple-person-up-user" className="h-5 w-5 text-muted-foreground" />}
          badges={[
            { label: `${facilities.reduce((sum, f) => sum + f.warnings, 0)} WARN`, className: 'bg-yellow-100 text-yellow-700' },
            { label: `${facilities.reduce((sum, f) => sum + f.defaults, 0)} DEFAULT`, className: 'bg-red-100 text-red-700' },
          ]}
        />
      </div>

      {/* Facility list */}
      <div>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg sm:text-xl font-semibold">
            Facilities
            <span className="ml-2 text-sm font-normal text-muted-foreground">({filteredFacilities.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:flex-initial">
              <Icon name="streamline:search-visual" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                placeholder="Search facilities..."
                className={`${INPUT_CLASS} pl-9 sm:w-64`}
              />
            </div>
            <div className="flex rounded-lg border border-border">
              <button onClick={() => setViewMode('grid')} className={`rounded-l-lg p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                <Icon name="streamline:interface-edit-grid-grid-layout-layouts-module" className="h-4 w-4" />
              </button>
              <button onClick={() => setViewMode('list')} className={`rounded-r-lg p-2 transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                <Icon name="streamline:task-list" className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="mb-4 inline-flex rounded-lg bg-muted p-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setStatusFilter(f.value); setPage(1); }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${statusFilter === f.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {f.label}
              <span className="ml-1.5 text-xs text-muted-foreground">{statusCounts[f.value]}</span>
            </button>
          ))}
        </div>

        {paginatedFacilities.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <p className="text-sm text-muted-foreground">
              {searchQuery.trim() ? `No facilities matching "${searchQuery}"` : 'No facilities in this category.'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {paginatedFacilities.map((facility) => <FacilityCard key={facility.id} facility={facility} />)}
          </div>
        ) : (
          <div className="space-y-3">
            {paginatedFacilities.map((facility) => <FacilityRow key={facility.id} facility={facility} />)}
          </div>
        )}

        {filteredFacilities.length > 0 && (
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-muted-foreground">Showing {rangeStart}–{rangeEnd} of {filteredFacilities.length} facilities</span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-muted-foreground">Per page</span>
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="rounded-md border border-input bg-background px-2 py-1 text-sm">
                  {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setPage(1)} disabled={safePage === 1} className="rounded-md border border-border px-2 py-1 text-sm text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors">First</button>
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors">
                    <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
                  </button>
                  <PageNumbers currentPage={safePage} totalPages={totalPages} onPageChange={setPage} />
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors">
                    <Icon name="streamline:interface-arrows-button-right-arrow-right-keyboard" className="h-4 w-4" />
                  </button>
                  <button onClick={() => setPage(totalPages)} disabled={safePage === totalPages} className="rounded-md border border-border px-2 py-1 text-sm text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors">Last</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────────────────

function PageNumbers({ currentPage, totalPages, onPageChange }: { currentPage: number; totalPages: number; onPageChange: (page: number) => void }) {
  const pages: (number | 'ellipsis')[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push('ellipsis');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
  }
  return (
    <div className="flex items-center gap-0.5">
      {pages.map((p, i) =>
        p === 'ellipsis' ? (
          <span key={`e-${i}`} className="px-1.5 text-sm text-muted-foreground">...</span>
        ) : (
          <button key={p} onClick={() => onPageChange(p)} className={`min-w-[28px] rounded-md px-1.5 py-1 text-sm font-medium transition-colors ${p === currentPage ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-accent'}`}>{p}</button>
        ),
      )}
    </div>
  );
}

function StatCard({ title, value, subtitle, icon, badges }: { title: string; value: string; subtitle?: string; icon: React.ReactNode; badges?: { label: string; className: string }[] }) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
      {badges && (
        <div className="mt-2 flex flex-wrap gap-1.5 sm:gap-2">
          {badges.map((badge) => <span key={badge.label} className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>{badge.label}</span>)}
        </div>
      )}
    </div>
  );
}

function FacilityCard({ facility }: { facility: Facility }) {
  const navigate = useNavigate();
  const status = statusConfig[facility.status];
  return (
    <div onClick={() => navigate(`/cf/${encodeURIComponent(facility.name)}`)} className={`cursor-pointer rounded-xl border p-6 hover:bg-accent/50 transition-colors ${facility.status === 'paused' ? 'border-yellow-300' : 'border-border'}`}>
      <div className="flex items-start justify-between">
        <h3 className="font-semibold">{facility.name}</h3>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}>{status.label}</span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-y-3">
        <div><div className="text-xs text-muted-foreground">TVL</div><div className="text-lg font-bold">{facility.tvl}</div></div>
        <div><div className="text-xs text-muted-foreground">Utilization</div><div className="text-lg font-bold">{facility.utilization}</div></div>
        <div><div className="text-xs text-muted-foreground">Active Accounts</div><div className="text-lg font-bold">{facility.activeAccounts}</div></div>
        <div><div className="text-xs text-muted-foreground">Interest Rate</div><div className="text-lg font-bold">{facility.interestRate}</div></div>
      </div>
      {(facility.warnings > 0 || facility.defaults > 0) && (
        <div className="mt-4 flex items-center gap-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
          <Icon name="streamline:warning-triangle" className="h-3.5 w-3.5 text-yellow-500" />
          {facility.warnings} WARN, {facility.defaults} DEFAULT
        </div>
      )}
    </div>
  );
}

function FacilityRow({ facility }: { facility: Facility }) {
  const navigate = useNavigate();
  const status = statusConfig[facility.status];
  return (
    <div onClick={() => navigate(`/cf/${encodeURIComponent(facility.name)}`)} className={`cursor-pointer rounded-xl border p-5 hover:bg-accent/50 transition-colors ${facility.status === 'paused' ? 'border-yellow-300' : 'border-border'}`}>
      <div className="hidden sm:flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="w-48 font-semibold">{facility.name}</h3>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}>{status.label}</span>
        </div>
        <div className="flex items-center gap-8 text-sm">
          <div><span className="text-muted-foreground">TVL</span> <span className="ml-1 font-semibold">{facility.tvl}</span></div>
          <div><span className="text-muted-foreground">Util</span> <span className="ml-1 font-semibold">{facility.utilization}</span></div>
          <div><span className="text-muted-foreground">Accounts</span> <span className="ml-1 font-semibold">{facility.activeAccounts}</span></div>
          <div><span className="text-muted-foreground">Rate</span> <span className="ml-1 font-semibold">{facility.interestRate}</span></div>
          {(facility.warnings > 0 || facility.defaults > 0) && (
            <div className="flex items-center gap-1 text-xs text-yellow-600"><Icon name="streamline:warning-triangle" className="h-3.5 w-3.5" />{facility.warnings}W / {facility.defaults}D</div>
          )}
        </div>
      </div>
      <div className="sm:hidden space-y-3">
        <div className="flex items-start justify-between">
          <h3 className="font-semibold">{facility.name}</h3>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}>{status.label}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-muted-foreground">TVL</span> <span className="ml-1 font-semibold">{facility.tvl}</span></div>
          <div><span className="text-muted-foreground">Util</span> <span className="ml-1 font-semibold">{facility.utilization}</span></div>
          <div><span className="text-muted-foreground">Accounts</span> <span className="ml-1 font-semibold">{facility.activeAccounts}</span></div>
          <div><span className="text-muted-foreground">Rate</span> <span className="ml-1 font-semibold">{facility.interestRate}</span></div>
        </div>
        {(facility.warnings > 0 || facility.defaults > 0) && (
          <div className="flex items-center gap-1 text-xs text-yellow-600"><Icon name="streamline:warning-triangle" className="h-3.5 w-3.5" />{facility.warnings} WARN, {facility.defaults} DEFAULT</div>
        )}
      </div>
    </div>
  );
}
